import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import { PATHS } from '@src/consts'
import type { ChallengeScope } from '@src/consts/challenge-scope'
import { useIntrospectChallengeSuccessToken } from '@src/hooks/queries/challenge'
import { challengeToken, getUserId } from '@src/utils'

type UseChallengeGuardOptions = {
  scope: ChallengeScope
  redirectUrl: string
  enabled?: boolean
}

type UseChallengeGuardResult = {
  shouldHideContent: boolean
}

export function useChallengeGuard({
  scope,
  redirectUrl,
  enabled = true,
}: UseChallengeGuardOptions): UseChallengeGuardResult {
  const router = useRouter()
  const hasChecked = useRef(false)
  const [shouldHideContent, setShouldHideContent] = useState(true)

  const introspectMutation = useIntrospectChallengeSuccessToken()

  useEffect(() => {
    if (!enabled) {
      return
    }
    if (hasChecked.current) {
      return
    }
    hasChecked.current = true

    const userId = getUserId()
    if (userId === null) {
      // When no userId exists in localStorage the user is not authenticated.
      // We intentionally skip the guard check and keep shouldHideContent true
      // so the protected content is never rendered for unauthenticated visitors.
      // Redirecting to sign-in is delegated to the existing useRedirectRules /
      // 401 interceptor; this hook does not duplicate that concern.
      return
    }

    const token = challengeToken.get(scope)
    const challengeUrl =
      PATHS.CHALLENGE +
      '?scope=' +
      scope +
      '&redirect_url=' +
      encodeURIComponent(redirectUrl)

    if (token === null) {
      void router.replace(challengeUrl)
      return
    }

    introspectMutation.mutate(
      {
        userId,
        introspectChallengeSuccessTokenRequest: { token },
      },
      {
        onSuccess: (res) => {
          // Tokens are stored per-scope key, but as a defence-in-depth measure
          // we also verify that the scope embedded in the JWT matches the
          // requested scope so that a token issued for a different scope can
          // never unlock a different-scope-protected page.
          if (res.data.valid && res.data.scope === scope) {
            setShouldHideContent(false)
          } else {
            challengeToken.remove(scope)
            void router.replace(challengeUrl)
          }
        },
        onError: () => {
          // On 5xx / network errors the token's validity is unknown, so we do
          // NOT remove it here. Keeping the token and redirecting to the
          // challenge page is the safe default; a successful verify will
          // overwrite the token with a fresh one.
          void router.replace(challengeUrl)
        },
      }
    )
    // router.query is not consumed, so waiting for router.isReady is unnecessary.
    // Run exactly once on mount (StrictMode-safe via hasChecked ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { shouldHideContent: enabled ? shouldHideContent : false }
}
